/**
 * UsedInApplications — "Used in these applications" section on
 * `/documents/:id`. Deferred from G3A CP2 by explicit scope
 * tightening; now landed in G3B CP2 with the reverse-index endpoint
 * in place.
 *
 * Data: `GET /api/documents/{doc_id}/used-in-applications` returns
 * `{doc_type, canonical_doc_type, saved_grants: [...]}`.
 *
 * Layout: renders below the PDF preview column so it has full
 * horizontal space for grant title + agency + link. Each row links
 * to `/grants/:id`.
 *
 * States:
 *   - loading  → subtle inline spinner
 *   - error    → non-blocking; hide the section (readiness surface
 *                should never break the doc detail page)
 *   - empty    → "This document is not currently required by any
 *                of your saved grants."
 *   - result   → list of {title, agency, pipeline_status, link}
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ClipboardCheck, ExternalLink, Loader2 } from "lucide-react";
import { api } from "@/api/client";

const PIPELINE_STATUS_LABELS = {
  interested: "Interested",
  preparing_documents: "Preparing documents",
  drafting_application: "Drafting application",
  submitted: "Submitted",
  awarded: "Awarded",
  declined: "Declined",
  not_pursued: "Not pursued",
};

export default function UsedInApplications({ docId }) {
  const [state, setState] = useState({ loading: true, data: null, error: null });

  useEffect(() => {
    if (!docId) return;
    let cancelled = false;
    setState({ loading: true, data: null, error: null });
    api
      .get(`/documents/${docId}/used-in-applications`)
      .then((res) => {
        if (!cancelled) setState({ loading: false, data: res.data, error: null });
      })
      .catch((err) => {
        if (!cancelled) setState({ loading: false, data: null, error: err });
      });
    return () => {
      cancelled = true;
    };
  }, [docId]);

  if (state.loading) {
    return (
      <section
        data-testid="used-in-applications-loading"
        className="mt-6 rounded-3xl p-4 flex items-center gap-2 text-xs text-v2-textMuted"
        style={{ background: "var(--v2-surface)", border: "1px solid var(--v2-border)" }}
      >
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
        Loading application references…
      </section>
    );
  }

  // Errors are non-blocking — this is an ancillary surface, not the
  // primary reason the user visited the page. Hide silently rather
  // than showing a scary banner on top of the file preview.
  if (state.error || !state.data) return null;

  const { saved_grants } = state.data;

  if (!saved_grants || saved_grants.length === 0) {
    return (
      <section
        data-testid="used-in-applications-empty"
        className="mt-6 rounded-3xl p-5"
        style={{ background: "var(--v2-surface)", border: "1px solid var(--v2-border)" }}
      >
        <div className="flex items-center gap-2">
          <ClipboardCheck className="w-4 h-4 text-v2-accent" />
          <p className="text-xs uppercase tracking-[0.28em] text-v2-textMuted">
            Used in these applications
          </p>
        </div>
        <p className="mt-3 text-sm text-v2-textMuted">
          This document is not currently required by any of your saved grants.
        </p>
      </section>
    );
  }

  return (
    <section
      data-testid="used-in-applications"
      className="mt-6 rounded-3xl p-5"
      style={{ background: "var(--v2-surface)", border: "1px solid var(--v2-border)" }}
    >
      <div className="flex items-center gap-2">
        <ClipboardCheck className="w-4 h-4 text-v2-accent" />
        <p className="text-xs uppercase tracking-[0.28em] text-v2-textMuted">
          Used in these applications
        </p>
        <span
          data-testid="used-in-applications-count"
          className="ml-1 text-[11px] text-v2-textMuted"
        >
          {saved_grants.length} saved grant{saved_grants.length === 1 ? "" : "s"}
        </span>
      </div>
      <ul className="mt-3">
        {saved_grants.map((g) => (
          <li
            key={g.grant_id}
            data-testid={`used-in-applications-row-${g.grant_id}`}
            className="py-3 flex items-start gap-3"
            style={{ borderTop: "1px dashed var(--v2-border)" }}
          >
            <div className="flex-1 min-w-0">
              <Link
                to={`/grants/${g.grant_id}`}
                data-testid={`used-in-applications-link-${g.grant_id}`}
                className="text-sm font-semibold text-v2-text hover:text-v2-accent inline-flex items-center gap-1"
              >
                {g.title}
                <ExternalLink className="w-3 h-3 opacity-60" />
              </Link>
              <p className="mt-0.5 text-[11px] text-v2-textMuted">
                {g.agency}
                {g.pipeline_status && (
                  <>
                    {" · "}
                    <span
                      data-testid={`used-in-applications-status-${g.grant_id}`}
                      className="uppercase tracking-[0.14em] text-v2-accent/80"
                    >
                      {PIPELINE_STATUS_LABELS[g.pipeline_status] || g.pipeline_status}
                    </span>
                  </>
                )}
              </p>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
